import type {
  Account,
  Claim,
  CompanyApplication,
  CompanyPolicy,
  CompanyProfile,
  EmployerForm,
  EmployeeForm,
  EmployeePolicy,
  EmployeeDependent,
  MedicalPlan,
  DentalPlan,
  VisionPlan,
  CarrierConfiguration,
  CompanyContribution,
} from "Utils/types";

export interface EntityState {
  entities: any;
}

export const ENTITY_PATHS = ["entities", "payload.responseSchema"];

interface Getter<T> {
  try(s: EntityState, id?: string): T | undefined;
  get(s: EntityState, id?: string): T;
  all(s: EntityState): Array<T>;
}

export function makeGetter<T>(table: string): Getter<T> {
  return {
    try: (s: EntityState, id?: string): T | undefined => {
      if (!id || !s.entities[table] || !s.entities[table][id]) {
        /**
         * Warn in development mode when a try getter is failing
         * @see https://docs.google.com/document/d/1OpVJsoAdZZRoR5bIThGDBK4fH5bmGC1LZkIVwqiTcjg/edit?usp=sharing
         */
        // if (process.env.NODE_ENV === "development") {
        //   console.warn(`tryGet{table}ById: Invalid ID for ${table}`);
        // }
        return undefined;
      }
      return s.entities[table][id];
    },
    get: (s: EntityState, id?: string): T => {
      if (id && s.entities[table] && s.entities[table][id]) {
        return s.entities[table][id];
      }
      throw new Error(`Invalid ID for ${table}`);
    },
    all: (s: EntityState) => {
      const val = s.entities[table];
      if (val) {
        return Object.values(s.entities[table]);
      }
      return [];
    },
  };
}

export const { try: tryGetClaimById, get: getClaimById, all: allClaims }: Getter<Claim> = makeGetter("claims");

export const { try: tryGetAccountById, get: getAccountById }: Getter<Account> = makeGetter("accounts");

export const { try: tryGetCompanyApplicationById, get: getCompanyApplicationById }: Getter<CompanyApplication> = makeGetter("companyApplications");

export const { try: tryGetCompanyPolicyById, get: getCompanyPolicyById, all: allCompanyPolicies }: Getter<CompanyPolicy> = makeGetter("companyPolicies");

export const { try: tryGetCompanyProfileById, get: getCompanyProfileById }: Getter<CompanyProfile> = makeGetter("companyProfiles");

export const { try: tryGetEmployerFormById, get: getEmployerFormById }: Getter<EmployerForm> = makeGetter("employerForms");

export const { try: tryGetEmployeePolicyById, get: getEmployeePolicyById, all: allEmployeePolicies }: Getter<EmployeePolicy> = makeGetter("employeePolicies");

export const { try: tryGetEmployeeFormById, get: getEmployeeFormById, all: allEmployeeForms }: Getter<EmployeeForm> = makeGetter("employeeForms");

export const { try: tryGetEmployeeDependentById, get: getEmployeeDependentById }: Getter<EmployeeDependent> = makeGetter("employeeDependents");

export const { try: tryGetMedicalPlanById, get: getMedicalPlanById, all: allMedicalPlans }: Getter<MedicalPlan> = makeGetter("medicalPlans");

export const { try: tryGetDentalPlanById, get: getDentalPlanById, all: allDentalPlans }: Getter<DentalPlan> = makeGetter("dentalPlans");

export const { try: tryGetVisionPlanById, get: getVisionPlanById, all: allVisionPlans }: Getter<VisionPlan> = makeGetter("visionPlans");

export const {
  try: tryGetCarrierConfigById,
  get: getCarrierConfigById,
  all: allCarrierConfigs,
}: Getter<CarrierConfiguration> = makeGetter("carrierConfigurations");

export const {
  try: tryGetRecommendationSurveyById,
  get: getRecommendationSurveyById,
  all: allRecommendationSurveys,
}: Getter<VisionPlan> = makeGetter("recommendationSurveys");

export const { all: allCompanyContributions }: Getter<CompanyContribution> = makeGetter("companyContributions");
